import React from "react";

import { UseCases } from "@planzone/core";
import { project } from "@planzone/dto";

import { Display } from "@common/display";
import { Popup } from "@common/popup";
import { Window } from "@common/window";
import { Resource } from "@scenes/resource";
import { ZoneCreation } from "@scenes/zoneCreation";
import { ProjectsTransfer } from "@scenes/projectsTransfer";
import { ZoneRemoval } from "@scenes/zoneRemoval";
import { ZoneEdition } from "@scenes/zoneEdition";
import { ProjectCreation } from "@scenes/projectCreation";
import { ListCreation } from "@scenes/listCreation";
import { TimeRecordCriteria } from "@scenes/timeRecordCriteria";
import { TimeRecordRemoval } from "@scenes/timeRecordRemoval";
import { TaskCreation } from "@scenes/taskCreation";
import { ResourceGroupEdition } from "@scenes/resourceGroupEdition";
import { ProjectResourceGroupRemoval } from "@scenes/projectResourceGroupRemoval";
import { TaskRemoval } from "@scenes/taskRemoval";
import { EventCreation } from "@scenes/eventCreation";
import { PlanzoneMaterialResourceAddition } from "@scenes/planzoneMaterialResourceAddition";
import { ProjectMaterialResourceAddition } from "@scenes/projectMaterialResourceAddition";
import { PlanzoneVirtualResourceAddition } from "@scenes/planzoneVirtualResourceAddition";
import { ProjectVirtualResourceAddition } from "@scenes/projectVirtualResourceAddition";
import { RemoveResourceFromProject } from "@scenes/projectResourceRemoval";
import { ProjectGroupCreation } from "@scenes/projectGroupCreation";
import { RemoveTaskStatus } from "@scenes/taskStatusRemoval";
import { ProjectExternalLinks } from "@scenes/projectExternalLinks";
import { Project } from "@scenes/project";
import { ListEdition } from "@scenes/listEdition";
import { ProjectRemoval } from "@scenes/projectRemoval";
import { ListRemoval } from "@scenes/listRemoval";
import { MilestoneCompletion } from "@scenes/milestoneCompletion";
import { MilestoneCreation } from "@scenes/milestoneCreation";
import { ProjectReportSubmission } from "@scenes/projectReportSubmission";
import { ProjectReport } from "@scenes/projectReport";
import { PlanzoneResourceGroupCreation } from "@scenes/planzoneResourceGroupCreation";
import { PlanzoneResourceToProject } from "@scenes/planzoneResourceToProject";
import { TaskCompletion } from "@scenes/taskCompletion";
import { PlanzoneResourceRemoval } from "@scenes/planzoneResourceRemoval";
import { ProjectResourceEdition } from "@scenes/projectResourceEdition";
import { ResourceEdition } from "@scenes/resourceEdition";
import { useMessage } from "@components/useMessage";
import { MilestoneRemoval } from "@scenes/milestoneRemoval";
import { TaskStatuses } from "@scenes/taskStatuses";
import { Event } from "@scenes/event";
import { EventRemoval } from "@scenes/eventRemoval";
import { CommentRemoval } from "@scenes/commentRemoval";
import { ProjectPortfolioCreation } from "@scenes/projectPortfolioCreation";
import { ProjectPortfolioEdition } from "@scenes/projectPortfolioEdition";
import { ProjectPortfolioRemoval } from "@scenes/projectPortfolioRemoval";
import { ProjectFromPortfolioRemoval } from "@scenes/projectFromPortfolioRemoval";
import { ProjectCreationFromModel } from "@scenes/projectCreationFromModel";
import { ProjectDuplication } from "@scenes/projectDuplication";
import { ProjectModelCreationFromProject } from "@scenes/projectModelCreationFromProject";
import { InviteMembers } from "@scenes/inviteMembers";
import { ProjectReportRemoval } from "@scenes/projectReportRemoval";
import { ListDuplication } from "@scenes/listDuplication";
import { DocumentUpload } from "@scenes/documentUpload";
import { FolderCreation } from "@scenes/folderCreation";
import { FolderRemoval } from "@scenes/folderRemoval";
import { DocumentRemoval } from "@scenes/documentRemoval";
import { MoveDocument } from "@scenes/moveDocument";
import { MoveFolder } from "@scenes/moveFolder";
import { MoveElement } from "@scenes/moveElement";
import { CostProfileCreation } from "@scenes/costProfileCreation";
import { CostProfileRemoval } from "@scenes/costProfileRemoval";
import { CostRemoval } from "@scenes/costRemoval";
import { CostProfileEdition } from "@scenes/costProfileEdition";
import { AttachDocuments } from "@scenes/attachDocuments";
import { UnavailabilityCreation } from "@scenes/unavailabilityCreation";
import { UnavailabilityEdition } from "@scenes/unavailabilityEdition";
import { UnavailabilityRemoval } from "@scenes/unavailabilityRemoval";
import { StrategicAxisCreation } from "@scenes/strategicAxisCreation";
import { OrganizationalUnitCreation } from "@scenes/organizationalUnitCreation";
import { OrganizationalUnitRemoval } from "@scenes/organizationalUnitRemoval";
import { OrganizationalUnitMove } from "@scenes/organizationalUnitMove";
import { OrganizationalUnitEdition } from "@scenes/organizationalUnitEdition";
import { StrategicAxisRemoval } from "@scenes/strategicAxisRemoval";
import { StrategicAxisEdition } from "@scenes/strategicAxisEdition";
import { Milestone } from "@scenes/milestone";
import { ProjectStrategicAxes } from "@scenes/projectStrategicAxes";
import { EditTaskStatus } from "@scenes/editTaskStatus";
import { MoveKanbanTask } from "@scenes/moveKanbanTask";
import { ConstrainElement } from "@scenes/constrainElement";
import { ConstraintEdition } from "@scenes/constraintEdition";
import { ConstraintRemoval } from "@scenes/constraintRemoval";
import { ProjectExpenseCreation } from "@scenes/projectExpenseCreation";
import { ProjectExpenseRemoval } from "@scenes/projectExpenseRemoval";
import { ProjectExpenseEdition } from "@scenes/projectExpenseEdition";
import { StartDiscussion } from "@scenes/startDiscussion";
import { Task } from "@scenes/task";
import { List } from "@scenes/list";
import { ProjectLogs } from "@scenes/projectLogs";
import { WbsConfiguration } from "@scenes/wbsConfiguration";
import { ProjectElementDetailsConfiguration } from "@scenes/projectElementDetailsConfiguration";
import { ResourceDetailsConfiguration } from "@scenes/resourceDetailsConfiguration";
import { ProjectListingConfiguration } from "@scenes/projectListingConfiguration";
import { ResourcesListingConfiguration } from "@scenes/resourcesListingConfiguration";
import { TimeRecordsListingConfiguration } from "@scenes/timeRecordsListingConfiguration";
import { ProjectMembersListingConfiguration } from "@scenes/projectMembersListingConfiguration";
import { ProjectDetailsConfiguration } from "@scenes/projectDetailsConfiguration";
import { EditSecondaryCriteriaTemplate } from "@scenes/editSecondaryCriteriaTemplate";
import { CalendarHolidaysCreation } from "@scenes/calendarHolidaysCreation";
import { CalendarHolidaysRemoval } from "@scenes/calendarHolidaysRemoval";
import { CalendarHolidaysEdition } from "@scenes/calendarHolidaysEdition";
import { ProjectResourceDeactivation } from "@scenes/projectResourceDeactivation";
import { ProjectResourceActivation } from "@scenes/projectResourceActivation";
import { DocumentCreation } from "@scenes/documentCreation";
import { FolderUpdate } from "@scenes/folderUpdate";
import { DocumentUpdate } from "@scenes/documentUpdate";
import { ResourceDeactivation } from "@scenes/resourceDeactivation";
import { ResourceActivation } from "@scenes/resourceActivation";
import { SecondaryCriteriaTemplate } from "@scenes/secondaryCriteriaTemplate";
import { CalendarExportLinks } from "@scenes/calendarExportLinks";
import { AddPlanzoneResourceToProjects } from "@scenes/addPlanzoneResourceToProjects";
import { AddIdentityProvider } from "@scenes/addIdentityProvider";
import { RemoveIdentityProvider } from "@scenes/removeIdentityProvider";
import { Props, ViewMap } from "./types";
import { Context, useModal, useViews } from "./utils";
import styles from "./styles.scss";
import { DiscussionRemoval } from "@scenes/discussionRemoval";
import { TransferProjectResourceAllocations } from "@scenes/transferProjectResourceAllocations";
import { AssignToTask } from "@scenes/assignToTask";
import { AllocationWarnings } from "@scenes/allocationWarnings";
import { ChatConversationRemoval } from "@scenes/chatConversationRemoval";

function Modal(props: Props) {
  const { views, removeView, pushView } = useViews();
  const map: ViewMap = {
    [UseCases.saveTimeRecord]: ({ timeRecord, isDisabled }) => (
      <Popup close={() => removeView(UseCases.saveTimeRecord)}>
        <TimeRecordCriteria
          timeRecord={timeRecord}
          isDisabled={isDisabled}
          close={() => removeView(UseCases.saveTimeRecord)}
        />
      </Popup>
    ),
    [UseCases.deleteTimeRecord]: (timeRecord) => (
      <Popup close={() => removeView(UseCases.deleteTimeRecord)}>
        <TimeRecordRemoval
          timeRecord={timeRecord}
          cancel={() => removeView(UseCases.deleteTimeRecord)}
        />
      </Popup>
    ),
    [UseCases.retrieveTask]: ({ task, isMultiProject }) => (
      <Window close={() => removeView(UseCases.retrieveTask)}>
        <Task
          close={() => removeView(UseCases.retrieveTask)}
          taskId={task.uuid}
          isMultiProject={isMultiProject}
        />
      </Window>
    ),
    [UseCases.retrieveEvent]: ({ event, isMultiProject }) => (
      <Window close={() => removeView(UseCases.retrieveEvent)}>
        <Event
          eventId={event.uuid}
          close={() => removeView(UseCases.retrieveEvent)}
          isMultiProject={isMultiProject}
        />
      </Window>
    ),
    [UseCases.retrieveMilestone]: ({ milestone, isMultiProject }) => (
      <Window close={() => removeView(UseCases.retrieveMilestone)}>
        <Milestone
          milestoneId={milestone.uuid}
          close={() => removeView(UseCases.retrieveMilestone)}
          isMultiProject={isMultiProject}
        />
      </Window>
    ),
    [UseCases.retrieveList]: ({ listId, isMultiProject }) => (
      <Window close={() => removeView(UseCases.retrieveList)}>
        <List
          listId={listId}
          isMultiProject={isMultiProject}
          close={() => removeView(UseCases.retrieveList)}
        />
      </Window>
    ),
    [UseCases.createZone]: (data) => (
      <Window close={() => removeView(UseCases.createZone)}>
        <div className={styles.zoneCreation}>
          <ZoneCreation
            planzone={data}
            cancel={() => removeView(UseCases.createZone)}
          />
        </div>
      </Window>
    ),
    [UseCases.createList]: ({ list, project }) => (
      <Popup close={() => removeView(UseCases.createList)}>
        <ListCreation
          list={list}
          project={project}
          cancel={() => removeView(UseCases.createList)}
        />
      </Popup>
    ),
    [UseCases.createProjectPortfolio]: () => (
      <Popup close={() => removeView(UseCases.createProjectPortfolio)}>
        <ProjectPortfolioCreation
          cancel={() => removeView(UseCases.createProjectPortfolio)}
        />
      </Popup>
    ),
    [UseCases.editProjectPortfolio]: (portfolio) => (
      <Popup close={() => removeView(UseCases.editProjectPortfolio)}>
        <ProjectPortfolioEdition
          portfolio={portfolio}
          cancel={() => removeView(UseCases.editProjectPortfolio)}
        />
      </Popup>
    ),
    [UseCases.removeProjectPortfolio]: (portfolio) => (
      <Popup close={() => removeView(UseCases.removeProjectPortfolio)}>
        <ProjectPortfolioRemoval
          portfolio={portfolio}
          cancel={() => removeView(UseCases.removeProjectPortfolio)}
        />
      </Popup>
    ),
    [UseCases.removeProjectFromPortfolio]: ({ portfolio, project }) => (
      <Popup close={() => removeView(UseCases.removeProjectFromPortfolio)}>
        <ProjectFromPortfolioRemoval
          portfolio={portfolio}
          project={project}
          cancel={() => removeView(UseCases.removeProjectFromPortfolio)}
        />
      </Popup>
    ),
    [UseCases.createMilestone]: ({
      list,
      project,
      isMultiProject,
      initialDate,
    }) => (
      <Popup close={() => removeView(UseCases.createMilestone)}>
        <MilestoneCreation
          list={list}
          project={project}
          isMultiProject={isMultiProject}
          initialDate={initialDate}
          cancel={() => removeView(UseCases.createMilestone)}
        />
      </Popup>
    ),
    [UseCases.editList]: ({ list, project }) => (
      <Popup close={() => removeView(UseCases.editList)}>
        <div className={styles.projectCreation}>
          <ListEdition
            project={project}
            list={list}
            cancel={() => removeView(UseCases.editList)}
          />
        </div>
      </Popup>
    ),
    [UseCases.createProject]: ({ zone, isTemplate, isMultiProject }) => (
      <Window close={() => removeView(UseCases.createProject)}>
        <div className={styles.project}>
          <ProjectCreation
            zone={zone}
            isMultiProject={isMultiProject}
            isTemplate={isTemplate || false}
            close={() => removeView(UseCases.createProject)}
          />
        </div>
      </Window>
    ),
    [UseCases.createProjectModelFromProject]: (project) => (
      <Window close={() => removeView(UseCases.createProjectModelFromProject)}>
        <div className={styles.project}>
          <ProjectModelCreationFromProject
            project={project}
            close={() => removeView(UseCases.createProjectModelFromProject)}
          />
        </div>
      </Window>
    ),
    [UseCases.useProjectModel]: ({ zone, model }) => (
      <Window close={() => removeView(UseCases.useProjectModel)}>
        <div className={styles.project}>
          <ProjectCreationFromModel
            zone={zone}
            model={model}
            close={() => removeView(UseCases.useProjectModel)}
          />
        </div>
      </Window>
    ),
    [UseCases.duplicateProject]: ({ zone, project }) => (
      <Window close={() => removeView(UseCases.duplicateProject)}>
        <div className={styles.project}>
          <ProjectDuplication
            zone={zone}
            sourceProject={project}
            close={() => removeView(UseCases.duplicateProject)}
          />
        </div>
      </Window>
    ),
    [UseCases.addResourceGroup]: ({ namespace, resourceType }) => (
      <Popup close={() => removeView(UseCases.addResourceGroup)}>
        {resourceType === "project" ? (
          <ProjectGroupCreation
            project={{ ...project, uuid: namespace }}
            cancel={() => removeView(UseCases.addResourceGroup)}
          />
        ) : (
          <PlanzoneResourceGroupCreation
            cancel={() => removeView(UseCases.addResourceGroup)}
          />
        )}
      </Popup>
    ),
    [UseCases.removeResourceFromProject]: ({ project, projectResource }) => (
      <Popup close={() => removeView(UseCases.removeResourceFromProject)}>
        <RemoveResourceFromProject
          project={project}
          projectResource={projectResource}
          cancel={() => removeView(UseCases.removeResourceFromProject)}
        />
      </Popup>
    ),
    [UseCases.transferProjectResourceAllocations]: ({
      project,
      projectResource,
    }) => (
      <Popup
        close={() => removeView(UseCases.transferProjectResourceAllocations)}
      >
        <TransferProjectResourceAllocations
          project={project}
          fromProjectResource={projectResource}
          cancel={() => removeView(UseCases.transferProjectResourceAllocations)}
        />
      </Popup>
    ),
    [UseCases.createTask]: ({
      project,
      list,
      task,
      isMultiProject,
      initialDate,
    }) => (
      <Window close={() => removeView(UseCases.createTask)}>
        <div className={styles.projectResourceGroupRemoval}>
          <TaskCreation
            list={list}
            project={project}
            task={task}
            isMultiProject={isMultiProject}
            initialDate={initialDate}
            cancel={() => removeView(UseCases.createTask)}
          />
        </div>
      </Window>
    ),
    [UseCases.createEvent]: ({
      project,
      list,
      isMultiProject,
      initialFormData,
      range,
    }) => (
      <Popup close={() => removeView(UseCases.createEvent)}>
        <EventCreation
          list={list}
          project={project}
          initialFormData={initialFormData}
          isMultiProject={isMultiProject}
          range={range}
          cancel={() => removeView(UseCases.createEvent)}
        />
      </Popup>
    ),
    [UseCases.editResourceGroup]: ({ group, namespace }) => (
      <Popup close={() => removeView(UseCases.editResourceGroup)}>
        <ResourceGroupEdition
          group={group}
          project={{ ...project, uuid: namespace }}
          cancel={() => removeView(UseCases.editResourceGroup)}
        />
      </Popup>
    ),
    [UseCases.removeResourceGroup]: (group) => (
      <Window close={() => removeView(UseCases.removeResourceGroup)}>
        <div className={styles.projectResourceGroupRemoval}>
          <ProjectResourceGroupRemoval
            group={group}
            cancel={() => removeView(UseCases.removeResourceGroup)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeTask]: (task) => (
      <Window close={() => removeView(UseCases.removeTask)}>
        <div className={styles.projectResourceGroupRemoval}>
          <TaskRemoval
            task={task}
            cancel={() => removeView(UseCases.removeTask)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeEvent]: (event) => (
      <Window close={() => removeView(UseCases.removeEvent)}>
        <div className={styles.projectResourceGroupRemoval}>
          <EventRemoval
            event={event}
            cancel={() => removeView(UseCases.removeEvent)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeCostProfile]: (costProfile) => (
      <Window close={() => removeView(UseCases.removeCostProfile)}>
        <div className={styles.projectResourceGroupRemoval}>
          <CostProfileRemoval
            costProfile={costProfile}
            cancel={() => removeView(UseCases.removeCostProfile)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeCost]: (cost) => (
      <Window close={() => removeView(UseCases.removeCostProfile)}>
        <div className={styles.projectResourceGroupRemoval}>
          <CostRemoval
            cost={cost}
            cancel={() => removeView(UseCases.removeCostProfile)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeFolder]: (folder) => (
      <Window close={() => removeView(UseCases.removeFolder)}>
        <div className={styles.projectResourceGroupRemoval}>
          <FolderRemoval
            folder={folder}
            cancel={() => removeView(UseCases.removeFolder)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeDocument]: (document) => (
      <Window close={() => removeView(UseCases.removeDocument)}>
        <div className={styles.projectResourceGroupRemoval}>
          <DocumentRemoval
            document={document}
            cancel={() => removeView(UseCases.removeDocument)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeProjectReport]: ({ project, projectReport }) => (
      <Window close={() => removeView(UseCases.removeProjectReport)}>
        <div className={styles.projectResourceGroupRemoval}>
          <ProjectReportRemoval
            project={project}
            projectReport={projectReport}
            cancel={() => removeView(UseCases.removeProjectReport)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeMilestone]: (milestone) => (
      <Window close={() => removeView(UseCases.removeMilestone)}>
        <div className={styles.projectResourceGroupRemoval}>
          <MilestoneRemoval
            milestone={milestone}
            cancel={() => removeView(UseCases.removeMilestone)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeProject]: (project) => (
      <Window close={() => removeView(UseCases.removeProject)}>
        <div className={styles.projectRemoval}>
          <ProjectRemoval
            project={project}
            cancel={() => removeView(UseCases.removeProject)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeList]: ({ list }) => (
      <Window close={() => removeView(UseCases.removeList)}>
        <div className={styles.listRemoval}>
          <div>
            <ListRemoval
              list={list}
              cancel={() => removeView(UseCases.removeList)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.addPlanzoneMaterialResource]: () => (
      <Window close={() => removeView(UseCases.addPlanzoneMaterialResource)}>
        <div className={styles.planzoneResourceAddition}>
          <PlanzoneMaterialResourceAddition
            cancel={() => removeView(UseCases.addPlanzoneMaterialResource)}
          />
        </div>
      </Window>
    ),
    [UseCases.addProjectMaterialResource]: (project) => (
      <Window close={() => removeView(UseCases.addProjectMaterialResource)}>
        <div className={styles.planzoneResourceAddition}>
          <ProjectMaterialResourceAddition
            project={project}
            cancel={() => removeView(UseCases.addProjectMaterialResource)}
          />
        </div>
      </Window>
    ),
    [UseCases.addPlanzoneVirtualResource]: () => (
      <Window close={() => removeView(UseCases.addPlanzoneVirtualResource)}>
        <div className={styles.planzoneResourceAddition}>
          <PlanzoneVirtualResourceAddition
            cancel={() => removeView(UseCases.addPlanzoneVirtualResource)}
          />
        </div>
      </Window>
    ),
    [UseCases.inviteMembers]: () => (
      <Window close={() => removeView(UseCases.inviteMembers)}>
        <div className={styles.planzoneResourceAddition}>
          <InviteMembers cancel={() => removeView(UseCases.inviteMembers)} />
        </div>
      </Window>
    ),
    [UseCases.addProjectVirtualResource]: (project) => (
      <Window close={() => removeView(UseCases.addProjectVirtualResource)}>
        <div className={styles.planzoneResourceAddition}>
          <ProjectVirtualResourceAddition
            project={project}
            cancel={() => removeView(UseCases.addProjectVirtualResource)}
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveProject]: ({
      project,
      isMultiProject,
      autoFocusDescription,
    }) => (
      <Window close={() => removeView(UseCases.retrieveProject)}>
        <div className={styles.project}>
          <Project
            project={project}
            autoFocusDescription={autoFocusDescription}
            isMultiProject={isMultiProject}
            close={() => removeView(UseCases.retrieveProject)}
          />
        </div>
      </Window>
    ),
    [UseCases.editZone]: (zone) => (
      <Popup close={() => removeView(UseCases.editZone)}>
        <div className={styles.zoneEdition}>
          <ZoneEdition
            zone={zone}
            cancel={() => removeView(UseCases.editZone)}
          />
        </div>
      </Popup>
    ),
    [UseCases.removeZone]: (zone) => (
      <Popup close={() => removeView(UseCases.removeZone)}>
        <ZoneRemoval
          zone={zone}
          cancel={() => removeView(UseCases.removeZone)}
        />
      </Popup>
    ),
    [UseCases.removeTaskStatus]: ({ project, status, hasNoTasks }) => (
      <Popup close={() => removeView(UseCases.removeTaskStatus)}>
        <div className={styles.removeTaskStatus}>
          <RemoveTaskStatus
            project={project}
            status={status}
            cancel={() => removeView(UseCases.removeTaskStatus)}
            hasNoTasks={hasNoTasks}
          />
        </div>
      </Popup>
    ),
    [UseCases.transferProjects]: ({ zone, projects }) => (
      <Window close={() => removeView(UseCases.transferProjects)}>
        <div className={styles.projectsTransfer}>
          <div className={styles.projectsTransferMain}>
            <ProjectsTransfer
              zone={zone}
              projects={projects}
              cancel={() => removeView(UseCases.transferProjects)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.addProjectExternalLink]: (project) => (
      <Window close={() => removeView(UseCases.addProjectExternalLink)}>
        <div className={styles.projectExternalLinks}>
          <ProjectExternalLinks
            project={project}
            close={() => removeView(UseCases.addProjectExternalLink)}
          />
        </div>
      </Window>
    ),
    [UseCases.markMilestoneAsCompleted]: (milestone) => (
      <Window close={() => removeView(UseCases.markMilestoneAsCompleted)}>
        <div className={styles.milestoneCompletion}>
          <div>
            <MilestoneCompletion
              milestone={milestone}
              cancel={() => removeView(UseCases.markMilestoneAsCompleted)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.markTaskAsCompleted]: ({ task, project }) => (
      <Window close={() => removeView(UseCases.markTaskAsCompleted)}>
        <div className={styles.milestoneCompletion}>
          <div>
            <TaskCompletion
              task={task}
              project={project}
              cancel={() => removeView(UseCases.markTaskAsCompleted)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.editTaskStatus]: ({ task, status, project }) => (
      <Window close={() => removeView(UseCases.editTaskStatus)}>
        <div className={styles.milestoneCompletion}>
          <div>
            <EditTaskStatus
              task={task}
              status={status}
              project={project}
              cancel={() => removeView(UseCases.editTaskStatus)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.moveKanbanTask]: ({ task, currentColumn, nextColumn, range }) => (
      <Window close={() => removeView(UseCases.moveKanbanTask)}>
        <div className={styles.milestoneCompletion}>
          <div>
            <MoveKanbanTask
              task={task}
              currentColumn={currentColumn}
              nextColumn={nextColumn}
              range={range}
              cancel={() => removeView(UseCases.moveKanbanTask)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.submitProjectReport]: ({ project, initialReport }) => (
      <Window close={() => removeView(UseCases.submitProjectReport)}>
        <div className={styles.popup}>
          <ProjectReportSubmission
            project={project}
            initialReport={initialReport}
            close={() => removeView(UseCases.submitProjectReport)}
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveProjectReport]: ({
      project,
      reportId,
      isMultiProject,
    }) => (
      <Window close={() => removeView(UseCases.retrieveProjectReport)}>
        <div className={styles.popup}>
          <ProjectReport
            reportId={reportId}
            project={project}
            isMultiProject={isMultiProject}
            close={() => removeView(UseCases.retrieveProjectReport)}
          />
        </div>
      </Window>
    ),
    [UseCases.addPlanzoneResourceToProject]: (project) => (
      <Window close={() => removeView(UseCases.addPlanzoneResourceToProject)}>
        <div className={styles.planzoneResourceToProject}>
          <div>
            <PlanzoneResourceToProject
              cancel={() => removeView(UseCases.addPlanzoneResourceToProject)}
              project={project}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.removeResourceFromPlanzone]: (resource) => (
      <Window close={() => removeView(UseCases.removeResourceFromPlanzone)}>
        <div className={styles.planzoneResourceRemoval}>
          <PlanzoneResourceRemoval
            cancel={() => removeView(UseCases.removeResourceFromPlanzone)}
            resource={resource}
          />
        </div>
      </Window>
    ),
    [UseCases.removeComment]: (comment) => (
      <Window close={() => removeView(UseCases.removeComment)}>
        <div className={styles.commentRemoval}>
          <CommentRemoval
            cancel={() => removeView(UseCases.removeComment)}
            comment={comment}
          />
        </div>
      </Window>
    ),
    [UseCases.removeDiscussion]: (discussion) => (
      <Window close={() => removeView(UseCases.removeDiscussion)}>
        <div className={styles.discussionRemoval}>
          <DiscussionRemoval
            cancel={() => removeView(UseCases.removeDiscussion)}
            discussion={discussion}
          />
        </div>
      </Window>
    ),
    [UseCases.moveListElement]: (data) => (
      <Window close={() => removeView(UseCases.moveListElement)}>
        <div className={styles.resourceEdition}>
          <div>
            <MoveElement
              {...data}
              cancel={() => removeView(UseCases.moveListElement)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.createCostProfile]: () => (
      <Window close={() => removeView(UseCases.createCostProfile)}>
        <div className={styles.resourceEdition}>
          <div>
            <CostProfileCreation
              cancel={() => removeView(UseCases.createCostProfile)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.editCostProfile]: (costProfile) => (
      <Window close={() => removeView(UseCases.editCostProfile)}>
        <div className={styles.resourceEdition}>
          <div>
            <CostProfileEdition
              costProfile={costProfile}
              cancel={() => removeView(UseCases.editCostProfile)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.retrieveResource]: (id) => (
      <Window close={() => removeView(UseCases.retrieveResource)}>
        <div className={styles.resourceEdition}>
          <div className={styles.resourceEditionWrapper}>
            <ResourceEdition
              id={id}
              cancel={() => removeView(UseCases.retrieveResource)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.editProjectResource]: (data) => (
      <Window close={() => removeView(UseCases.editProjectResource)}>
        <div className={styles.projectResourceEdition}>
          <ProjectResourceEdition
            projectResource={data}
            cancel={() => removeView(UseCases.editProjectResource)}
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveTaskStatuses]: (project) => (
      <Window close={() => removeView(UseCases.retrieveTaskStatuses)}>
        <div className={styles.taskStatuses}>
          <TaskStatuses
            project={project}
            cancel={() => removeView(UseCases.retrieveTaskStatuses)}
          />
        </div>
      </Window>
    ),
    [UseCases.uploadDocument]: ({ namespace, type, folder }) => (
      <Window close={() => removeView(UseCases.uploadDocument)}>
        <div className={styles.uploadDocument}>
          <DocumentUpload
            namespace={namespace}
            type={type}
            folder={folder}
            close={() => removeView(UseCases.uploadDocument)}
          />
        </div>
      </Window>
    ),
    [UseCases.createFolder]: (data) => (
      <Window close={() => removeView(UseCases.createFolder)}>
        <div className={styles.folderCreation}>
          <FolderCreation
            {...data}
            cancel={() => removeView(UseCases.createFolder)}
          />
        </div>
      </Window>
    ),
    [UseCases.createDocument]: ({
      documentType,
      type,
      namespace,
      projectName,
    }) => (
      <Window close={() => removeView(UseCases.createDocument)}>
        <div className={styles.documentCreation}>
          <DocumentCreation
            documentType={documentType}
            namespace={namespace}
            type={type}
            projectName={projectName}
            cancel={() => removeView(UseCases.createDocument)}
          />
        </div>
      </Window>
    ),
    [UseCases.attachDocumentsToElement]: (data) => (
      <Window close={() => removeView(UseCases.attachDocumentsToElement)}>
        <div className={styles.selectDocuments}>
          <AttachDocuments
            {...data}
            cancel={() => removeView(UseCases.attachDocumentsToElement)}
          />
        </div>
      </Window>
    ),
    [UseCases.moveDocument]: ({ document, type, namespace, projectName }) => (
      <Window close={() => removeView(UseCases.moveDocument)}>
        <div className={styles.moveDocument}>
          <MoveDocument
            document={document}
            type={type}
            namespace={namespace}
            projectName={projectName}
            close={() => removeView(UseCases.moveDocument)}
          />
        </div>
      </Window>
    ),
    [UseCases.moveFolder]: ({ folder, type, namespace, projectName }) => (
      <Window close={() => removeView(UseCases.moveFolder)}>
        <div className={styles.moveFolder}>
          <MoveFolder
            folder={folder}
            type={type}
            namespace={namespace}
            projectName={projectName}
            close={() => removeView(UseCases.moveFolder)}
          />
        </div>
      </Window>
    ),
    [UseCases.createUnavailability]: () => (
      <Window close={() => removeView(UseCases.createUnavailability)}>
        <div className={styles.createUnavailability}>
          <UnavailabilityCreation
            cancel={() => removeView(UseCases.createUnavailability)}
          />
        </div>
      </Window>
    ),
    [UseCases.editUnavailability]: (unavailability) => (
      <Window close={() => removeView(UseCases.editUnavailability)}>
        <div className={styles.createUnavailability}>
          <UnavailabilityEdition
            unavailability={unavailability}
            cancel={() => removeView(UseCases.editUnavailability)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeUnavailability]: (unavailability) => (
      <Window close={() => removeView(UseCases.removeUnavailability)}>
        <div className={styles.createUnavailability}>
          <UnavailabilityRemoval
            unavailability={unavailability}
            cancel={() => removeView(UseCases.removeUnavailability)}
          />
        </div>
      </Window>
    ),
    [UseCases.createStrategicAxis]: () => (
      <Window close={() => removeView(UseCases.createStrategicAxis)}>
        <div className={styles.createUnavailability}>
          <StrategicAxisCreation
            cancel={() => removeView(UseCases.createStrategicAxis)}
          />
        </div>
      </Window>
    ),
    [UseCases.createOrganizationalUnit]: (data) => (
      <Popup close={() => removeView(UseCases.createOrganizationalUnit)}>
        <OrganizationalUnitCreation
          parent={data.parent}
          cancel={() => removeView(UseCases.createOrganizationalUnit)}
        />
      </Popup>
    ),
    [UseCases.removeOrganizationalUnit]: (data) => (
      <Window close={() => removeView(UseCases.removeOrganizationalUnit)}>
        <div className={styles.createUnavailability}>
          <OrganizationalUnitRemoval
            organizationalUnit={data.organizationalUnit}
            cancel={() => removeView(UseCases.removeOrganizationalUnit)}
          />
        </div>
      </Window>
    ),
    [UseCases.moveOrganizationalUnit]: (data) => (
      <Window close={() => removeView(UseCases.moveOrganizationalUnit)}>
        <div className={styles.createUnavailability}>
          <OrganizationalUnitMove
            organizationalUnit={data.organizationalUnit}
            cancel={() => removeView(UseCases.moveOrganizationalUnit)}
          />
        </div>
      </Window>
    ),
    [UseCases.updateOrganizationalUnit]: (data) => (
      <Popup close={() => removeView(UseCases.updateOrganizationalUnit)}>
        <OrganizationalUnitEdition
          organizationalUnit={data.organizationalUnit}
          cancel={() => removeView(UseCases.updateOrganizationalUnit)}
        />
      </Popup>
    ),
    [UseCases.removeStrategicAxis]: (strategicAxis) => (
      <Window close={() => removeView(UseCases.removeStrategicAxis)}>
        <div className={styles.createUnavailability}>
          <StrategicAxisRemoval
            strategicAxis={strategicAxis}
            cancel={() => removeView(UseCases.removeStrategicAxis)}
          />
        </div>
      </Window>
    ),
    [UseCases.editStrategicAxis]: (strategicAxis) => (
      <Window close={() => removeView(UseCases.editStrategicAxis)}>
        <div className={styles.createUnavailability}>
          <StrategicAxisEdition
            strategicAxis={strategicAxis}
            cancel={() => removeView(UseCases.editStrategicAxis)}
          />
        </div>
      </Window>
    ),
    [UseCases.createProjectStrategicAxis]: (project) => (
      <Window close={() => removeView(UseCases.createProjectStrategicAxis)}>
        <div className={styles.createProjectStrategicAxis}>
          <div>
            <ProjectStrategicAxes
              project={project}
              cancel={() => removeView(UseCases.createProjectStrategicAxis)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.duplicateList]: (data) => (
      <Window close={() => removeView(UseCases.duplicateList)}>
        <div className={styles.duplicateList}>
          <div>
            <ListDuplication
              {...data}
              cancel={() => removeView(UseCases.duplicateList)}
            />
          </div>
        </div>
      </Window>
    ),
    [UseCases.constrainElements]: (data) => (
      <Window close={() => removeView(UseCases.constrainElements)}>
        <div className={styles.createUnavailability}>
          <ConstrainElement
            {...data}
            cancel={() => removeView(UseCases.constrainElements)}
          />
        </div>
      </Window>
    ),
    [UseCases.editConstraint]: (data) => (
      <Window close={() => removeView(UseCases.editConstraint)}>
        <div className={styles.createUnavailability}>
          <ConstraintEdition
            {...data}
            cancel={() => removeView(UseCases.editConstraint)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeConstraint]: (data) => (
      <Window close={() => removeView(UseCases.removeConstraint)}>
        <div className={styles.createUnavailability}>
          <ConstraintRemoval
            constraint={data}
            cancel={() => removeView(UseCases.removeConstraint)}
          />
        </div>
      </Window>
    ),
    [UseCases.createProjectExpense]: (project) => (
      <Window close={() => removeView(UseCases.createProjectExpense)}>
        <div className={styles.createUnavailability}>
          <ProjectExpenseCreation
            project={project}
            cancel={() => removeView(UseCases.createProjectExpense)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeProjectExpense]: (projectExpense) => (
      <Window close={() => removeView(UseCases.removeProjectExpense)}>
        <div className={styles.createUnavailability}>
          <ProjectExpenseRemoval
            projectExpense={projectExpense}
            cancel={() => removeView(UseCases.removeProjectExpense)}
          />
        </div>
      </Window>
    ),
    [UseCases.editProjectExpense]: ({ project, projectExpense }) => (
      <Window close={() => removeView(UseCases.editProjectExpense)}>
        <div className={styles.createUnavailability}>
          <ProjectExpenseEdition
            project={project}
            projectExpense={projectExpense}
            cancel={() => removeView(UseCases.editProjectExpense)}
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveProjectLogs]: (projectId) => (
      <Window close={() => removeView(UseCases.retrieveProjectLogs)}>
        <div className={styles.retrieveProjectLogs}>
          <ProjectLogs
            projectId={projectId}
            close={() => removeView(UseCases.retrieveProjectLogs)}
          />
        </div>
      </Window>
    ),
    [UseCases.editSecondaryCriteriaTemplate]: (data) => (
      <Window close={() => removeView(UseCases.editSecondaryCriteriaTemplate)}>
        <div className={styles.createUnavailability}>
          <EditSecondaryCriteriaTemplate
            {...data}
            cancel={() => removeView(UseCases.editSecondaryCriteriaTemplate)}
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveWbsConfiguration]: ({ projectId }) => (
      <Window close={() => removeView(UseCases.retrieveWbsConfiguration)}>
        <div className={styles.viewConfiguration}>
          <WbsConfiguration
            projectId={projectId}
            cancel={() => removeView(UseCases.retrieveWbsConfiguration)}
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveResourceDetailsConfiguration]: () => (
      <Window
        close={() => removeView(UseCases.retrieveResourceDetailsConfiguration)}
      >
        <div className={styles.viewConfiguration}>
          <ResourceDetailsConfiguration
            cancel={() =>
              removeView(UseCases.retrieveResourceDetailsConfiguration)
            }
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveProjectElementDetailsConfiguration]: ({ project }) => (
      <Window
        close={() =>
          removeView(UseCases.retrieveProjectElementDetailsConfiguration)
        }
      >
        <div className={styles.viewConfiguration}>
          <ProjectElementDetailsConfiguration
            project={project}
            cancel={() =>
              removeView(UseCases.retrieveProjectElementDetailsConfiguration)
            }
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveProjectListingConfiguration]: () => (
      <Window
        close={() => removeView(UseCases.retrieveProjectListingConfiguration)}
      >
        <div className={styles.viewConfiguration}>
          <ProjectListingConfiguration
            cancel={() =>
              removeView(UseCases.retrieveProjectListingConfiguration)
            }
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveResourcesListingConfiguration]: () => (
      <Window
        close={() => removeView(UseCases.retrieveResourcesListingConfiguration)}
      >
        <div className={styles.viewConfiguration}>
          <ResourcesListingConfiguration
            cancel={() =>
              removeView(UseCases.retrieveResourcesListingConfiguration)
            }
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveTimeRecordsListingConfiguration]: () => (
      <Window
        close={() =>
          removeView(UseCases.retrieveTimeRecordsListingConfiguration)
        }
      >
        <div className={styles.viewConfiguration}>
          <TimeRecordsListingConfiguration
            cancel={() =>
              removeView(UseCases.retrieveTimeRecordsListingConfiguration)
            }
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveProjectMembersListingConfiguration]: ({ project }) => (
      <Window
        close={() =>
          removeView(UseCases.retrieveProjectMembersListingConfiguration)
        }
      >
        <div className={styles.viewConfiguration}>
          <ProjectMembersListingConfiguration
            project={project}
            cancel={() =>
              removeView(UseCases.retrieveProjectMembersListingConfiguration)
            }
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveProjectDetailsConfiguration]: (project) => (
      <Window
        close={() => removeView(UseCases.retrieveProjectDetailsConfiguration)}
      >
        <div className={styles.viewConfiguration}>
          <ProjectDetailsConfiguration
            project={project}
            cancel={() =>
              removeView(UseCases.retrieveProjectDetailsConfiguration)
            }
          />
        </div>
      </Window>
    ),
    [UseCases.createCalendarHolidays]: () => (
      <Window close={() => removeView(UseCases.createCalendarHolidays)}>
        <div className={styles.createCalendarHolidays}>
          <CalendarHolidaysCreation
            cancel={() => removeView(UseCases.createCalendarHolidays)}
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveCalendarExportLinks]: () => (
      <Window close={() => removeView(UseCases.retrieveCalendarExportLinks)}>
        <div className={styles.calendarLinkExport}>
          <CalendarExportLinks
            cancel={() => removeView(UseCases.retrieveCalendarExportLinks)}
          />
        </div>
      </Window>
    ),
    [UseCases.addPlanzoneResourceToProjects]: (resource) => (
      <Window close={() => removeView(UseCases.addPlanzoneResourceToProjects)}>
        <div className={styles.addPlanzoneResourceToProjects}>
          <AddPlanzoneResourceToProjects
            resource={resource}
            cancel={() => removeView(UseCases.addPlanzoneResourceToProjects)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeCalendarHolidays]: (calendarHolidays) => (
      <Window close={() => removeView(UseCases.removeCalendarHolidays)}>
        <div className={styles.removeCalendarHolidays}>
          <CalendarHolidaysRemoval
            calendarHolidays={calendarHolidays}
            cancel={() => removeView(UseCases.removeCalendarHolidays)}
          />
        </div>
      </Window>
    ),
    [UseCases.editCalendarHolidays]: (calendarHolidays) => (
      <Window close={() => removeView(UseCases.editCalendarHolidays)}>
        <div className={styles.editCalendarHolidays}>
          <CalendarHolidaysEdition
            calendarHolidays={calendarHolidays}
            cancel={() => removeView(UseCases.editCalendarHolidays)}
          />
        </div>
      </Window>
    ),
    [UseCases.disableProjectResource]: ({ project, projectResource }) => (
      <Window close={() => removeView(UseCases.disableProjectResource)}>
        <div className={styles.projectResourceDeactivation}>
          <ProjectResourceDeactivation
            projectResource={projectResource}
            cancel={() => removeView(UseCases.disableProjectResource)}
          />
        </div>
      </Window>
    ),
    [UseCases.activateProjectResource]: (data) => (
      <Window close={() => removeView(UseCases.activateProjectResource)}>
        <div className={styles.projectResourceActivation}>
          <ProjectResourceActivation
            projectResource={data}
            cancel={() => removeView(UseCases.activateProjectResource)}
          />
        </div>
      </Window>
    ),
    [UseCases.disableResource]: (data) => (
      <Window close={() => removeView(UseCases.disableResource)}>
        <div className={styles.resourceDeactivation}>
          <ResourceDeactivation
            planzoneResource={data}
            cancel={() => removeView(UseCases.disableResource)}
          />
        </div>
      </Window>
    ),
    [UseCases.activateResource]: (data) => (
      <Window close={() => removeView(UseCases.activateResource)}>
        <div className={styles.resourceActivation}>
          <ResourceActivation
            planzoneResource={data}
            cancel={() => removeView(UseCases.activateResource)}
          />
        </div>
      </Window>
    ),
    [UseCases.updateFolder]: ({ folder, type, namespace }) => (
      <Popup close={() => removeView(UseCases.updateFolder)}>
        <FolderUpdate
          folder={folder}
          type={type}
          namespace={namespace}
          close={() => removeView(UseCases.updateFolder)}
        />
      </Popup>
    ),
    [UseCases.updateDocument]: ({ folder, type, namespace, document }) => (
      <Popup close={() => removeView(UseCases.updateDocument)}>
        <DocumentUpdate
          folder={folder}
          type={type}
          namespace={namespace}
          document={document}
          close={() => removeView(UseCases.updateDocument)}
        />
      </Popup>
    ),
    [UseCases.startDiscussion]: (project) => (
      <Window close={() => removeView(UseCases.startDiscussion)}>
        <div className={styles.resourceActivation}>
          <StartDiscussion
            project={project}
            cancel={() => removeView(UseCases.startDiscussion)}
          />
        </div>
      </Window>
    ),
    [UseCases.retrieveSecondaryCriteriaTemplate]: (data) => (
      <Window
        close={() => removeView(UseCases.retrieveSecondaryCriteriaTemplate)}
      >
        <div className={styles.secondaryCriteria}>
          <SecondaryCriteriaTemplate
            {...data}
            close={() => removeView(UseCases.retrieveSecondaryCriteriaTemplate)}
          />
        </div>
      </Window>
    ),
    [UseCases.addIdentityProvider]: () => (
      <Window close={() => removeView(UseCases.addIdentityProvider)}>
        <div className={styles.addIdentityProvider}>
          <AddIdentityProvider
            cancel={() => removeView(UseCases.addIdentityProvider)}
          />
        </div>
      </Window>
    ),
    [UseCases.removeIdentityProvider]: (provider) => (
      <Window close={() => removeView(UseCases.removeIdentityProvider)}>
        <div className={styles.removeIdentityProvider}>
          <RemoveIdentityProvider
            identityProvider={provider}
            cancel={() => removeView(UseCases.removeIdentityProvider)}
          />
        </div>
      </Window>
    ),
    [UseCases.checkAllocationWarnings]: ({
      warnings,
      date,
      onConfirm,
      onClose,
    }) => (
      <Popup
        close={() => {
          onClose?.();
          removeView(UseCases.checkAllocationWarnings);
        }}
      >
        <AllocationWarnings
          warnings={warnings}
          date={date}
          onConfirm={() => {
            onConfirm();
            removeView(UseCases.checkAllocationWarnings);
          }}
          close={() => {
            onClose?.();
            removeView(UseCases.checkAllocationWarnings);
          }}
        />
      </Popup>
    ),
    [UseCases.allocateResources]: (data) => (
      <Window close={() => removeView(UseCases.allocateResources)}>
        <div className={styles.assignToTask}>
          <AssignToTask
            cancel={() => removeView(UseCases.allocateResources)}
            {...data}
          />
        </div>
      </Window>
    ),
    [UseCases.deleteConversation]: (conversation) => (
      <Popup close={() => removeView(UseCases.deleteConversation)}>
        <ChatConversationRemoval
          conversation={conversation}
          cancel={() => removeView(UseCases.deleteConversation)}
        />
      </Popup>
    ),
  };

  useMessage(
    ({ type }) => {
      const interpretedUseCase = type.replace(/Succeeded$/, "");

      interpretedUseCase !== UseCases.retrieveTask &&
        interpretedUseCase !== UseCases.retrieveList &&
        interpretedUseCase !== UseCases.retrieveProject &&
        interpretedUseCase !== UseCases.editResourceGroup &&
        interpretedUseCase !== UseCases.retrieveMilestone &&
        interpretedUseCase !== UseCases.retrieveProjectReport &&
        interpretedUseCase !== UseCases.retrieveTaskStatuses &&
        interpretedUseCase !== UseCases.retrieveEvent &&
        interpretedUseCase !== UseCases.retrieveResource &&
        interpretedUseCase !== UseCases.editCostProfile &&
        interpretedUseCase !== UseCases.createProjectStrategicAxis &&
        interpretedUseCase !== UseCases.editResource &&
        interpretedUseCase !== UseCases.checkAllocationWarnings &&
        interpretedUseCase !== UseCases.retrieveProjectLogs &&
        interpretedUseCase !== UseCases.createProjectExpense &&
        interpretedUseCase !== UseCases.editProjectExpense &&
        interpretedUseCase !== UseCases.retrieveSecondaryCriteriaTemplate &&
        removeView(interpretedUseCase as UseCases);

      interpretedUseCase === UseCases.removeTask &&
        removeView(UseCases.retrieveTask);
      interpretedUseCase === UseCases.removeEvent &&
        removeView(UseCases.retrieveEvent);
      interpretedUseCase === UseCases.removeUnavailability &&
        removeView(UseCases.editUnavailability);
      interpretedUseCase === UseCases.removeMilestone &&
        removeView(UseCases.retrieveMilestone);
      interpretedUseCase === UseCases.removeProject &&
        removeView(UseCases.retrieveProject);
      interpretedUseCase === UseCases.removeList &&
        removeView(UseCases.retrieveList);
      interpretedUseCase === UseCases.removeProjectReport &&
        removeView(UseCases.retrieveProjectReport);
      interpretedUseCase === UseCases.removeConstraint &&
        removeView(UseCases.editConstraint);
      interpretedUseCase === UseCases.editTask &&
        removeView(UseCases.editTaskStatus);
      (interpretedUseCase === UseCases.addPlanzoneResourcesToProject ||
        interpretedUseCase === UseCases.addEveryoneToProject ||
        interpretedUseCase === UseCases.addPlanzoneResourceToProject) &&
        removeView(UseCases.addPlanzoneResourceToProject);
      interpretedUseCase === UseCases.createProjectExpense &&
        removeView(UseCases.createProjectExpense);
      interpretedUseCase === UseCases.editProjectExpense &&
        removeView(UseCases.editProjectExpense);
      interpretedUseCase === UseCases.editWbsConfiguration &&
        removeView(UseCases.retrieveWbsConfiguration);
      interpretedUseCase === UseCases.editProjectDetailsConfiguration &&
        removeView(UseCases.retrieveProjectDetailsConfiguration);
      interpretedUseCase === UseCases.editResourceDetailsConfiguration &&
        removeView(UseCases.retrieveResourceDetailsConfiguration);
      interpretedUseCase === UseCases.editProjectElementDetailsConfiguration &&
        removeView(UseCases.retrieveProjectElementDetailsConfiguration);
      interpretedUseCase === UseCases.editProjectListingConfiguration &&
        removeView(UseCases.retrieveProjectListingConfiguration);
      interpretedUseCase === UseCases.editResourcesListingConfiguration &&
        removeView(UseCases.retrieveResourcesListingConfiguration);
      interpretedUseCase === UseCases.editTimeRecordsListingConfiguration &&
        removeView(UseCases.retrieveTimeRecordsListingConfiguration);
      interpretedUseCase === UseCases.editProjectMembersListingConfiguration &&
        removeView(UseCases.retrieveProjectMembersListingConfiguration);
    },
    { localOnly: true },
  );

  return (
    <Context.Provider value={{ views, pushView }}>
      {props.children}
      {views.map(({ key, data }) => (
        <React.Fragment key={key}>{map[key](data as any)}</React.Fragment>
      ))}
    </Context.Provider>
  );
}

export { Modal, useModal };
